import type { GroupMemberRole } from '@group/domain';
import { GroupConsoleTitle } from '../common';
import { Button } from '@framework/ui/atoms/Button';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { EmailParseResult } from './EmailAddressParser';
import { createGroupMemberInvitations, MAX_INVITATION_COUNT } from './createGroupMemberInvitations';
import { SignUpMethodName } from '@group/domain/GroupEntity';
import { MemberInvitationEmailInput } from './MemberInvitationEmailInput';
import { useCurrentUserEntity } from '@user/UserEntity/useCurrentUserEntity';
import { toast } from 'react-hot-toast';
import { useAssignedWorkspaces } from '@workspace/ui/WorkspaceList';
import type { SelectedWorkspaceItem } from './InvitePageWorkspaceList';
import { InvitePageWorkspaceList } from './InvitePageWorkspaceList';
import { InvitationRoleSelect } from './InvitationRoleSelect';
import { WorkspaceMemberRole } from '@workspace/domain/workspace';
import { TRPCClientError } from '@trpc/client';
import { createErrorMessage } from '@framework/utils/createErrorMessage';
import { useGroupConsoleOutletContext } from '../WithGroupConsoleMenu';
import { useTranslation } from 'react-i18next';

export const GroupConsoleInvitePage: React.FC = () => {
  const { t } = useTranslation();
  const { group } = useGroupConsoleOutletContext();
  const currentUserEntity = useCurrentUserEntity();
  const workspaces = useAssignedWorkspaces(currentUserEntity?.id || null, group.id);
  const [emails, setEmails] = useState<EmailParseResult[]>([]);
  const [role, setRole] = useState<GroupMemberRole>('member');
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<SelectedWorkspaceItem[]>([]);
  const [message, setMessage] = useState<string>('');
  const [sending, setSending] = useState(false);
  const hasInvalidAddress = useMemo(() => emails.some(({ valid }) => !valid), [emails]);

  const isValidWorkspaces = role === 'guest' ? selectedWorkspaces.length <= 1 : true;

  useEffect(() => {
    // グループへの招待ロールが guest に変更された場合には、追加ワークスペースに管理者を設定していたものを編集者に変更する
    if (role === 'guest') {
      setSelectedWorkspaces((prev) =>
        prev.map(({ workspaceId, role }) => ({
          workspaceId,
          role: role === WorkspaceMemberRole.admin ? WorkspaceMemberRole.editor : role,
        }))
      );
    }
  }, [role]);

  const sendable =
    emails.length > 0 && emails.length <= MAX_INVITATION_COUNT && !hasInvalidAddress && isValidWorkspaces;

  const filteredWorkspaces = useMemo(() => {
    if (workspaces) {
      return workspaces.filter((ws) => !ws.isPersonal && !ws.setting.isPublicSpace);
    } else {
      return [];
    }
  }, [workspaces]);

  const availableSignUpMethods = useMemo(() => group.setting.availableSignUpMethods, [group]);

  const handleAddEmails = useCallback((addedEmails: EmailParseResult[]) => {
    setEmails((emails) => [...emails, ...addedEmails]);
  }, []);

  const handleRemoveEmail = useCallback((removeEmail: string) => {
    setEmails((emails) => emails.filter(({ email }) => email != removeEmail));
  }, []);

  const handleRemoveInvalidEmails = useCallback(() => {
    setEmails((emails) => emails.filter(({ valid }) => valid));
  }, []);

  const handleChangeMessage = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(event.target.value);
  }, []);

  const handleWorkspaceChange = useCallback((workspaces: SelectedWorkspaceItem[]) => {
    setSelectedWorkspaces(workspaces);
  }, []);

  const handleInvite = useCallback(async () => {
    if (!currentUserEntity) {
      toast.error(t('group.invite.toast.noSender'));
      return;
    }

    if (!sendable) {
      toast.error(t('group.invite.toast.invalidEmail'));
      return;
    }

    setSending(true);

    const emailAddresses = emails.map(({ email }) => email);
    // メッセージに改行があれば改行タグに置換
    const convertedMessage = message.replace(/\n/g, '<br>');
    try {
      await createGroupMemberInvitations(
        group,
        currentUserEntity,
        emailAddresses,
        role,
        selectedWorkspaces,
        convertedMessage
      );
      setEmails([]);
      setRole('member');
      setMessage('');
      setSelectedWorkspaces([]);

      toast.success(t('group.invite.toast.success'));
    } catch (e) {
      if (e instanceof TRPCClientError) {
        toast.error(createErrorMessage(e, t('group.invite.toast.error')));
      } else {
        throw e;
      }
    } finally {
      setSending(false);
    }
  }, [currentUserEntity, emails, sendable, message, group, role, selectedWorkspaces, t]);

  return (
    <div className="flex max-w-156 flex-col">
      <GroupConsoleTitle title={t('group.console.nav.invite')} />
      <div className="mb-4">
        <div className="mb-2 font-bold">{t('group.invite.emailSection.label')}</div>
        <MemberInvitationEmailInput
          emails={emails}
          disabled={sending}
          onAddEmails={handleAddEmails}
          onRemoveEmail={handleRemoveEmail}
          onRemoveInvalidEmails={handleRemoveInvalidEmails}
        />
      </div>
      <div className="mb-4">
        <div className="mb-2 font-bold">{t('group.members.table.role')}</div>
        <InvitationRoleSelect defaultRole="member" onChange={setRole} disabled={sending} />
      </div>
      <div className="mb-4">
        <div className="mb-2 font-bold">{t('group.invite.message.label')}</div>
        <textarea
          value={message}
          onChange={handleChangeMessage}
          className="w-full rounded-sm border border-gray-500 p-2"
          rows={5}
          disabled={sending}
          placeholder={t('group.applyLicense.form.messagePlaceholder')}
        />
      </div>
      <div className="mb-4">
        {t('group.invite.notice.nameAndEmail')}
        <br />
        {t('group.invite.notice.loginMethods')}
        <ul className="list-inside list-disc px-6">
          {SignUpMethodName.values().map((name) => (
            <li key={name}>
              {t(SignUpMethodName.getLocalStringKey(name))} (
              {availableSignUpMethods.availableOf(name)
                ? t('group.invite.signUpMethod.available')
                : t('group.invite.signUpMethod.unavailable')}
              )
            </li>
          ))}
        </ul>
      </div>

      <div className="mb-4">
        <div className="mb-2 font-bold">{t('group.invite.workspaceSection.label')}</div>
        {!isValidWorkspaces && (
          <div className="my-2 text-red-700">{t('group.invite.workspaceSection.guestWarning')}</div>
        )}
        <InvitePageWorkspaceList
          workspaces={filteredWorkspaces}
          selectedWorkspaces={selectedWorkspaces}
          onChange={handleWorkspaceChange}
          excludeAdminRole={role === 'guest'}
        />
      </div>

      <Button color="brand" className="ml-auto disabled:cursor-not-allowed" onClick={handleInvite} disabled={!sendable}>
        {t('group.invite.submit')}
      </Button>
    </div>
  );
};
